/**
 * How long this dish has been waiting, cooking, or standing ready.
 *
 * The waiting clock starts when the wave is called into the kitchen, never when
 * the order was typed: a dish in a wave the waiter has not sent in is not late,
 * and a clock that says otherwise cries wolf all service.
 */
import type { DelayTier } from "@shared/kds/table-board";
import { fmtClock } from "../lib/dish-row";
import { useTranslation } from "../i18n";

interface DishRowTimersProps {
  waitSecs: number | null;
  prepSecs: number | null;
  readySecs: number | null;
  status: string;
  delayTier: DelayTier;
}

const CHIP = "inline-flex items-center gap-1 px-2 py-0.5 rounded-md tabular-nums text-kds-meta";

export default function DishRowTimers({
  waitSecs,
  prepSecs,
  readySecs,
  status,
  delayTier,
}: DishRowTimersProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5 mt-1">
      {waitSecs !== null && (
        <span
          className={[
            CHIP,
            delayTier === "overdue"
              ? "bg-red-500/25 text-red-400"
              : delayTier === "warn"
                ? "bg-yellow-400/25 text-yellow-300"
                : "bg-kds-muted/20 text-kds-text",
          ].join(" ")}
          title={t("kds.timers.sinceWaveCalled", "Tempo dalla chiamata dell'ondata")}
        >
          ⏱ {fmtClock(waitSecs)}
        </span>
      )}
      {prepSecs !== null && status === "PREPARING" && (
        <span className={`${CHIP} bg-kds-preparing/25 text-kds-preparing`} title={t("kds.timers.sinceStart", "Tempo da «Inizia»")}>
          ▶ {fmtClock(prepSecs)}
        </span>
      )}
      {readySecs !== null && status === "READY" && (
        <span className={`${CHIP} bg-kds-ready/25 text-kds-ready`} title={t("kds.timers.sinceReady", "Tempo da «Pronto»")}>
          ✓ {fmtClock(readySecs)}
        </span>
      )}
    </div>
  );
}
